import { ComponentChildren } from "preact"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table"
import { cn } from "../../../utils"
import { fmt_int, fmt_pct0, fmt_pct1, fmt_signed_pct, fmt_usd2, fmt_usd_short, trade_review_summary } from "./trade_review_model"

export const WORKING_COLOR = "#2a78d6"
export const RETAILER_NW_COLOR = "#eb6834"
export const KEHE_OPS_COLOR = "#1baf7a"
export const MUTED_COLOR = "#b4b2aa"
export const BASELINE_COLOR = "#6b6a65"
export const GRID_COLOR = "#e7e6e1"

/** One figure from the summary, formatted, so a sentence never holds a typed number. */
export type figure_format = "usd_short" | "usd2" | "int" | "pct0" | "pct1" | "signed_pct"

const FORMATTERS: Record<figure_format, (value: number) => string> = {
  usd_short: fmt_usd_short,
  usd2: fmt_usd2,
  int: fmt_int,
  pct0: fmt_pct0,
  pct1: fmt_pct1,
  signed_pct: fmt_signed_pct,
}

export function Figure({ summary, field, as }: { summary: trade_review_summary; field: string; as: figure_format }) {
  return <span class="font-medium tabular-nums">{FORMATTERS[as](summary[field] ?? 0)}</span>
}

export function Panel({ title, note, id, children, className }: { title: ComponentChildren; note?: ComponentChildren; id?: string; children: ComponentChildren; className?: string }) {
  return (
    <section id={id} class={cn("rounded-lg border bg-card p-5 space-y-3 min-w-0", className)}>
      <div class="space-y-1">
        <h2 class="text-base font-medium">{title}</h2>
        {note && <p class="text-sm text-muted-foreground max-w-3xl">{note}</p>}
      </div>
      {children}
    </section>
  )
}

export function Tile({ label, value, note }: { label: string; value: ComponentChildren; note: ComponentChildren }) {
  return (
    <div class="rounded-lg border bg-card p-4 space-y-1 min-w-0">
      <div class="text-sm text-muted-foreground">{label}</div>
      <div class="text-2xl tabular-nums">{value}</div>
      <div class="text-sm text-muted-foreground">{note}</div>
    </div>
  )
}

export function Finding({ title, children }: { title: ComponentChildren; children: ComponentChildren }) {
  return (
    <article class="rounded-lg border bg-card p-4 space-y-1 min-w-0">
      <h3 class="text-sm font-medium">{title}</h3>
      <p class="text-sm text-muted-foreground">{children}</p>
    </article>
  )
}

export function Legend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div class="flex flex-wrap gap-4 text-sm text-muted-foreground">
      {items.map((item: { label: string; color: string; dashed?: boolean }) => (
        <span key={item.label} class="inline-flex items-center gap-1.5">
          {item.dashed ? (
            <span class="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: item.color }} />
          ) : (
            <span class="inline-block size-3 rounded-sm" style={{ background: item.color }} />
          )}
          {item.label}
        </span>
      ))}
    </div>
  )
}

export function InlineBar({ share, label, color }: { share: number; label: string; color?: string }) {
  return (
    <span class="inline-flex items-center gap-2">
      <span class="inline-block h-2.5 rounded-sm" style={{ width: `${Math.max(2, Math.min(1, share) * 90)}px`, background: color ?? WORKING_COLOR }} />
      {label}
    </span>
  )
}

export type column<T> = { header: string; left?: boolean; wrap?: boolean; cell: (row: T) => ComponentChildren }

/** A plain data table: numbers right-aligned and tabular, scrolling inside its own box. */
export function DataTable<T>({ columns, rows, row_key, tall }: { columns: column<T>[]; rows: T[]; row_key: (row: T) => string; tall?: boolean }) {
  return (
    <div class={cn("overflow-auto", tall && "max-h-[26rem]")}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col: column<T>) => (
              <TableHead key={col.header} class={cn("whitespace-nowrap sticky top-0 bg-card px-3", col.left ? "text-left" : "text-right")}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row: T) => (
            <TableRow key={row_key(row)}>
              {columns.map((col: column<T>) => (
                <TableCell key={col.header} class={cn("tabular-nums px-3 py-2 text-sm", col.left ? "text-left" : "text-right", col.wrap ? "min-w-56" : "whitespace-nowrap")}>
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

export function ChartTooltip({ lines }: { lines: string[] }) {
  return (
    <div class="rounded-md border bg-popover px-2.5 py-1.5 text-xs shadow-md">
      {lines.map((line: string, index: number) => (
        <div key={line} class={index === 0 ? "text-muted-foreground" : undefined}>
          {line}
        </div>
      ))}
    </div>
  )
}

/** A recharts tooltip drawn from the hovered row. Recharts' own prop types don't reach a custom `content`. */
export function row_tooltip<T>(lines: (row: T) => string[]) {
  return function RowTooltip(props: { active?: boolean; payload?: { payload: T }[] }) {
    const row = props.active ? props.payload?.[0]?.payload : undefined
    return row ? <ChartTooltip lines={lines(row)} /> : null
  }
}
